import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowRight, ArrowLeft, Clock, ShieldCheck, CheckCircle2, Check } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { getArticle } from '../data/articles';
import { articleSchema, faqSchema, breadcrumbSchema } from '../lib/seoSchemas';

const mdComponents = {
  h2: ({ node, ...p }) => <h2 className="cn-display text-2xl md:text-3xl text-cn-text mt-12 mb-4" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-display font-semibold text-xl text-cn-text mt-8 mb-3" {...p} />,
  p: ({ node, ...p }) => <p className="text-cn-text-muted leading-relaxed text-base md:text-lg mb-5" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc ml-5 space-y-2.5 mb-6 marker:text-cn-primary" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal ml-5 space-y-2.5 mb-6 marker:text-cn-primary marker:font-semibold" {...p} />,
  li: ({ node, ...p }) => <li className="text-cn-text-muted leading-relaxed pl-1" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-cn-text" {...p} />,
  a: ({ node, ...p }) => <a className="text-cn-primary underline underline-offset-2 hover:text-cn-accent" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-4 border-cn-accent bg-cn-soft/50 rounded-r-2xl pl-5 pr-4 py-4 my-7 text-cn-text font-medium leading-relaxed" {...p} />
  ),
};

export default function ArticlePage() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/help" replace />;

  const path = `/resources/${a.slug}`;
  const related = a.related.map(getArticle).filter(Boolean);
  const schema = [
    articleSchema({
      title: a.title, description: a.metaDescription, slug: a.slug, image: a.heroImage,
      datePublished: a.datePublished, dateModified: a.dateModified,
    }),
    a.faqs?.length ? faqSchema(a.faqs) : null,
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Resources', path: '/resources' },
      { name: a.title, path },
    ]),
  ].filter(Boolean);

  const prettyDate = new Date(a.dateModified || a.datePublished).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="article-page">
      <Seo title={a.metaTitle} description={a.metaDescription} path={path} image={a.heroImage} keywords={a.keywords} schema={schema} />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/help">Resources</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage className="line-clamp-1">{a.category}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Header */}
      <article className="cn-container pt-8 pb-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="cn-eyebrow !mb-0 text-cn-accent">{a.category}</span>
            <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readMinutes} min read</span>
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl text-cn-text mb-5 leading-tight">{a.title}</h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">{a.excerpt}</p>
          <div className="mt-5 text-sm text-cn-text-muted">
            By <span className="text-cn-text font-medium">The CaraLoom Care Team</span> · Updated {prettyDate}
          </div>
        </div>

        {/* Hero image */}
        <div className="mt-8 rounded-3xl overflow-hidden cn-card aspect-[16/8] max-w-5xl">
          <img src={a.heroImage} alt={a.heroAlt} className="w-full h-full object-cover" loading="eager" />
        </div>

        {/* Body + sidebar */}
        <div className="mt-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 max-w-3xl" data-testid="article-body">
            <ReactMarkdown components={mdComponents}>{a.body}</ReactMarkdown>

            {/* FAQ */}
            {a.faqs?.length > 0 && (
              <div className="mt-14">
                <h2 className="cn-display text-2xl md:text-3xl text-cn-text mb-6">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {a.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`article-faq-${i}`}>
                      <AccordionTrigger className="text-left text-cn-text font-medium">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-cn-text-muted leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* Sticky CTA sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="cn-card rounded-3xl p-6 bg-cn-soft/40">
                <h3 className="font-display font-semibold text-lg text-cn-text mb-2">Need care now?</h3>
                <p className="text-sm text-cn-text-muted mb-4 leading-relaxed">
                  Get matched with verified, background-checked clinicians near you in about a minute.
                </p>
                <Link to="/auth" className="cn-btn cn-btn-primary w-full justify-center" data-testid="article-cta-get-matched">
                  Get matched <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/patient/find-clinicians" className="cn-btn cn-btn-ghost w-full justify-center mt-2" data-testid="article-cta-browse">
                  Browse clinicians
                </Link>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
                  <span className="cn-trust-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Background-checked</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-12">
          <Link to="/help" className="text-sm text-cn-primary font-semibold inline-flex items-center gap-1" data-testid="article-back-link">
            <ArrowLeft className="w-4 h-4" /> All resources
          </Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="cn-container pb-20">
          <h2 className="cn-display text-2xl text-cn-text mb-6">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/resources/${r.slug}`} className="cn-card cn-card-hover rounded-3xl overflow-hidden block group" data-testid={`related-article-${r.slug}`}>
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={r.heroImage} alt={r.heroAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                </div>
                <div className="p-5">
                  <span className="cn-eyebrow !mb-1 text-cn-accent">{r.category}</span>
                  <h3 className="font-semibold text-cn-text leading-snug">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
